import { type DetailedHTMLProps, type ElementType, type HTMLAttributes, type Ref } from 'react';

import { cn } from '@/lib/utils';

import { getDefaultTextWeight, textVariantClasses } from '../Text/Text';
import {
  type TextColorType,
  type TextVariantType,
  type TextWeightType,
  type TitleLevelType,
} from '../types';

type TitleHTMLType = DetailedHTMLProps<HTMLAttributes<HTMLHeadingElement>, HTMLHeadingElement>;

const levelClasses: Record<string, string> = {
  1: cn('text-[32px] leading-10'),
  2: cn('text-[26px] leading-8 tracking-[-0.03em]'),
  3: cn('text-2xl leading-8'),
  4: cn('text-[22px] leading-7'),
  5: cn('text-[18px] leading-6'),
  6: cn('text-[16px] leading-5'),
};

const colorClasses = {
  black: cn('text-gray-900'),
  green: cn('text-green-500'),
  red: cn('text-red-400'),
  white: cn('text-white'),
  grey: cn('text-gray-600'),
  'light-green': cn('text-primary'),
  'dark-grey': cn('text-gray-900'),
  'dark-green': cn('text-green-700'),
};

const weightClasses = {
  'extra-light': cn('font-extralight'),
  light: cn('font-light'),
  regular: cn('font-normal'),
  medium: cn('font-medium'),
  'semi-bold': cn('font-semibold'),
  bold: cn('font-bold'),
  'extra-bold': cn('font-extrabold'),
  'heavy-bold': cn('font-black'),
};

export type TitleProps = TitleHTMLType & {
  ref?: Ref<HTMLHeadingElement>;
  level: TitleLevelType;
  variant?: TextVariantType;
  color?: TextColorType;
  weight?: TextWeightType;
  inline?: boolean;
};

const Title = ({
  ref,
  className,
  level,
  variant,
  color = 'black',
  weight,
  inline,
  ...rest
}: TitleProps) => {
  const Element = `h${level}` as ElementType;
  const levelKey = String(level);

  return (
    <Element
      {...rest}
      ref={ref}
      className={cn(
        'font-sans',
        variant ? textVariantClasses[variant] : levelClasses[levelKey],
        colorClasses[color],
        weightClasses[weight ?? (variant ? getDefaultTextWeight(variant) : 'regular')],
        inline && 'inline-block',
        className,
      )}
    />
  );
};

export default Title;
